<template>
  <div>
    <div class="text-center mb-50">
      <p class="text-24 md:text-47 font-bold">Invite your team</p>
      <p class="text-20 text-grey-20">You can’t collaborate without your team members.</p>
    </div>

    <InputModule
      v-for="(teamMember, itemName, i) in teamMembersInputs"
      ref="teamMembersListRef"
      :key="`team-member-input-${i}`"
      v-model="teamMembersInputs[itemName]"
      :name="`teamMember${i}`"
      type="email"
      label="Email address"
      :placeholder="`teammate${i + 1}@company.com`"
      class="w-full mt-20"
      input-classes="px-22 py-13 rounded-10 border border-grey-10 focus:border-brand anim-all"
      label-class="mb-5 font-bold"
      shadow="focus:shadow-signup"
      :has-background="false"
      :error-message="errorMessages[itemName]"
      @clearErrorMessage="name => errorMessages[name] = null"
    />

    <ButtonModule
      text="add more fields"
      prefix="icon-add text-10"
      class="text-brand text-15 mt-10"
      @click.prevent="addTeamMember"
    />

    <ButtonModule
      text="Invite team members"
      class="w-full justify-center bg-brand hover:bg-brand-hover text-white rounded-10 mt-50 py-16 font-medium text-17"
      @click="invite"
    />

    <div class="flex justify-center items-center border-t border-hover w-full mt-60">
      <ButtonModule
        text="Skip this for now"
        class="text-brand text-15 group"
        suffix="relative icon-arrow-right ml-10 mt-2 text-10 left-0 group-hover:left-7 anim-all"
        @click.prevent="skipForNow"
      />
    </div>

    <SignupLoading v-if="showLastStep" :initial-value="51" />
  </div>
</template>

<script>
import { defineComponent, reactive, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useGtag } from 'vue-gtag-next'

import InputModule from '@/components/shared/form-controls/InputModule'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import SignupLoading from '@/components/auth/SignupLoading'

import { emailValidation } from '@/helpers'
import { teamsService, settingsService } from '@/services'

export default defineComponent({
  name: 'SignUpTeams',

  components: { InputModule, ButtonModule, SignupLoading },

  props: {
    team: Object,
    user: Object,
    hasAccount: (String || null)
  },

  emits: ['next'],

  setup (props, { emit }) {
    const { commit, dispatch } = useStore()
    const router = useRouter()
    const { event } = useGtag()

    const teamId = ref(null)

    const showLastStep = ref(false)

    const teamMembersListRef = ref('')

    const errorMessages = reactive({
      teamMember0: null,
      teamMember1: null,
      teamMember2: null
    })

    const teamMembersInputs = reactive({
      teamMember0: '',
      teamMember1: '',
      teamMember2: ''
    })

    onMounted(() => {
      commit('setRequestCount', 0)
    })

    /* METHODS */
    const addTeamMember = () => {
      const teamMembersInputsLength = Object.keys(teamMembersInputs).length

      teamMembersInputs[`teamMember${teamMembersInputsLength}`] = ''
      errorMessages[`teamMember${teamMembersInputsLength}`] = null
    }

    async function skipForNow () {
      commit('showWelcomeModal', true)
      showLastStep.value = true

      if (props.hasAccount) {
        await register()
      } else {
        await registerAndCreateUser()
      }

      router.push({ name: 'TeamBooks', params: { team: props.team.name } })
    }

    async function invite () {
      const invitedUsers = []
      Object.entries(teamMembersInputs).forEach(([key, value]) => {
        if (value === '') return

        if (!errorMessages[key]) {
          const error = emailValidation(teamMembersInputs[key])

          if (!error) {
            invitedUsers.push(value)
          } else {
            errorMessages[key] = error
          }
        }
      })

      if (Object.values(errorMessages).every(i => !i)) {
        showLastStep.value = true

        if (props.hasAccount) {
          await register()
        } else {
          await registerAndCreateUser()
        }
        if (teamId.value && invitedUsers.length > 0) {
          teamsService.inviteMembers({ emails: invitedUsers, teamId: teamId.value })
            .then(() => {
              commit('showWelcomeModal', true)
              router.push({ name: 'TeamBooks', params: { team: props.team.name } })
            })
        }
      }
    }

    async function registerAndCreateUser () {
      const data = await dispatch('registerTeam', {
        firstName: props.user.firstName,
        lastName: props.user.lastName,
        email: props.user.email,
        password: props.user.password,
        teamName: props.team.name
      })
      teamId.value = data.team.id
      event('conversion', { send_to: 'AW-597169833/rbwSCLzh_OQCEKmt4JwC' })
      await settingsService.update({ mode: 'Paper shape' })
      // window.fbq('trackCustom', 'signupTeams')
      if (props.team.logo) {
        setTimeout(() => commit('loading', true), 200)
        await teamsService.upload(props.team.logo).then(res => commit('setLogo', res.data))
      }

      router.push({ name: 'TeamBooks', params: { team: props.team.name } })
    }

    async function register () {
      dispatch('createTeam', props.team.name)
        .then(async ({ team }) => {
          teamId.value = team.id
          if (props.team.logo) {
            setTimeout(() => commit('loading', true), 200)
            await teamsService.upload(props.team.logo).then(res => commit('setLogo', res.data))
          }
          event('conversion', { send_to: 'AW-597169833/rbwSCLzh_OQCEKmt4JwC' })
          settingsService.update({ mode: 'Paper shape' })
          // window.fbq('trackCustom', 'signupTeams')
          router.push({ name: 'TeamBooks', params: { team: team.link } })
        })
    }

    return {
      // refs
      teamMembersListRef,

      // data
      showLastStep,
      // reactives
      teamMembersInputs,
      errorMessages,

      // functions,
      addTeamMember,
      skipForNow,
      invite
    }
  }
})
</script>
